import { UseQueryOptions } from '@tanstack/react-query'

import { useEntityQuery } from 'src/queries/entity'
import { GenerateQueriesArgs, generateQueries } from 'src/queries/queryGenerator'
import { BuyerFilter, BuyerProduct, GroupProductLinesTag, ProductsOrdering } from 'src/types'
import { AuthorizationByGroupRule } from 'src/types/authorizationByGroupRule'

export const PRODUCTS_ENDPOINT = 'products'
export const PRODUCTS_QUERY_KEY = 'products'

export type ProductType = 'basic' | 'presale'

export interface BaseProductQueryParams {
  authorizationByGroupRule?: AuthorizationByGroupRule
  calibreId?: Id[]
  categoryId?: Id | Id[] | null
  deliverableDates?: ApiDate[]
  fishingPortCountry?: string[]
  id?: Id | Id[]
  isFavorite?: true | null
  isFrequent?: true | null
  labelId?: Id[]
  limit?: number
  offset?: number
  ordering?: ProductsOrdering | ProductsOrdering[]
  portId?: Id[]
  preparation?: boolean
  preparationId?: Id[]
  productLineTags?: GroupProductLinesTag[]
  q?: string
  quality?: string | string[]
  qualityCategoryId?: number[]
  selfService?: true | null
  sellerCompanyId?: Id | Id[]
  shippableDateFrom?: ApiDate
  shippableDateTo?: ApiDate
  showOutOfStock?: boolean
  verticalId?: Id | Id[]
}

export interface ProductsQueryParams extends BaseProductQueryParams {
  buyerCompanyUuid?: string
  type?: ProductType
}

export interface UseProductQueryArgs<TData> {
  marketplaceId?: Id
  productId?: Id
  queryOptions?: UseQueryOptions<BuyerProduct, Error, TData>
  queryParams?: BuyerFilter
}
export const useProductQuery = <TData = BuyerProduct>({
  marketplaceId,
  productId,
  queryOptions,
  queryParams,
}: UseProductQueryArgs<TData>) =>
  useEntityQuery<BuyerProduct, {}, TData>({
    endpoint: `marketplaces/${marketplaceId}/${PRODUCTS_ENDPOINT}`,
    entityId: productId,
    queryKey: PRODUCTS_QUERY_KEY,
    queryParams,
    queryOptions: {
      ...queryOptions,
      enabled: !!marketplaceId && queryOptions?.enabled,
    },
  })

const generateQueriesConfig: GenerateQueriesArgs<BuyerProduct, ProductsQueryParams> = {
  endpoint: PRODUCTS_ENDPOINT,
  queryKey: PRODUCTS_QUERY_KEY,
}

export const {
  usePaginatedEntitiesQuery: usePaginatedProductsQuery,
  useRecursiveQuery: useRecursiveProductsQuery,
} = generateQueries(generateQueriesConfig)
